import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "tableData", "columns", "emptyPlaceholderText", "size", "onRowClick", "rowHighlight", "statusProps", "gridTemplateColumns", "onLayoutChange", "selectableRows", "onSelectChange", "pagination", "toolbarProps", "defaultActiveRow", "defaultSelectedRows"];
import { useSort } from "@table-library/react-table-library/sort";
import { Body, Header, Table as ComposedTable } from "@table-library/react-table-library/table";
import { useTheme } from "@table-library/react-table-library/theme";
import React, { useMemo } from "react";
import { EMPTY_OBJ } from "../helpers/logic/empties";
import HeaderRow from "./components/HeaderRow";
import sortIcons from "./components/HeaderRow/constants";
import Pagination from "./components/Pagination";
import Row from "./components/Row";
import { TableStatus } from "./components/States/TableStatus";
import Toolbar from "./components/Toolbar";
import { SelectContext } from "./contexts/SelectContext";
import { useCells, useColumnSort, useLayout, usePagination, useSelectRowLogic } from "./hooks";
import baseTableStyles, { BaseTableWrapper } from "./styled";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var Table = /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
  var _ref$id = _ref.id,
    id = _ref$id === void 0 ? "default" : _ref$id,
    tableData = _ref.tableData,
    columns = _ref.columns,
    emptyPlaceholderText = _ref.emptyPlaceholderText,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? "default" : _ref$size,
    onRowClick = _ref.onRowClick,
    _ref$rowHighlight = _ref.rowHighlight,
    rowHighlight = _ref$rowHighlight === void 0 ? true : _ref$rowHighlight,
    _ref$statusProps = _ref.statusProps,
    statusProps = _ref$statusProps === void 0 ? {
      status: null
    } : _ref$statusProps,
    gridTemplateColumns = _ref.gridTemplateColumns,
    onLayoutChange = _ref.onLayoutChange,
    _ref$selectableRows = _ref.selectableRows,
    selectableRows = _ref$selectableRows === void 0 ? false : _ref$selectableRows,
    onSelectChange = _ref.onSelectChange,
    _ref$pagination = _ref.pagination,
    pagination = _ref$pagination === void 0 ? false : _ref$pagination,
    toolbarProps = _ref.toolbarProps,
    defaultActiveRow = _ref.defaultActiveRow,
    defaultSelectedRows = _ref.defaultSelectedRows,
    props = _objectWithoutProperties(_ref, _excluded);
  var status = statusProps.status,
    errorButtonProps = statusProps.errorButtonProps,
    errorMessage = statusProps.errorMessage,
    emptyMessage = statusProps.emptyMessage;
  var tableStatus = status || (tableData.length === 0 ? "empty" : null);
  var data = {
    nodes: tableStatus ? [] : tableData
  };
  var numOfCols = selectableRows ? columns.length + 1 : columns.length;
  var theme = useTheme(baseTableStyles(size, onRowClick, rowHighlight, gridTemplateColumns, numOfCols));
  var selectLogic = useSelectRowLogic(data, onSelectChange, defaultActiveRow, defaultSelectedRows);
  var _ref2 = usePagination(data, pagination) || EMPTY_OBJ,
    tablePagination = _ref2.tablePagination,
    rowsPerPageOptions = _ref2.rowsPerPageOptions;
  var layout = useLayout(onLayoutChange);
  var cells = useCells(columns, emptyPlaceholderText);
  var _useColumnSort = useColumnSort(columns),
    sortFns = _useColumnSort.sortFns,
    defaultSortFunction = _useColumnSort.defaultSortFunction;
  var sort = useSort(data, {
    state: defaultSortFunction || null
  }, {
    sortIcon: sortIcons,
    sortFns: sortFns
  });
  var selectContextValue = useMemo(function () {
    return {
      selectLogic: selectLogic,
      selectableRows: selectableRows
    };
  }, [selectLogic, selectableRows]);
  return /*#__PURE__*/_jsx(SelectContext.Provider, {
    value: selectContextValue,
    children: /*#__PURE__*/_jsxs(BaseTableWrapper, {
      children: [(selectableRows || toolbarProps) && /*#__PURE__*/_jsx(Toolbar, _extends({
        totalNumOfTableRows: tableData === null || tableData === void 0 ? void 0 : tableData.length
      }, toolbarProps)), /*#__PURE__*/_jsx(ComposedTable, _extends({
        data: data,
        theme: theme,
        pagination: tablePagination,
        ref: forwardedRef,
        layout: layout,
        sort: sort,
        select: selectLogic
      }, props, {
        children: function children(tableList) {
          return /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Header, {
              children: /*#__PURE__*/_jsx(HeaderRow, {
                tablePagination: tablePagination,
                columns: columns,
                tableStatus: tableStatus,
                data: data
              })
            }), /*#__PURE__*/_jsx(Body, {
              children: tableList === null || tableList === void 0 ? void 0 : tableList.map(function (row) {
                return /*#__PURE__*/_jsx(Row, {
                  row: row,
                  onRowClick: onRowClick,
                  cells: cells
                }, row.id);
              })
            })]
          });
        }
      })), tableStatus && /*#__PURE__*/_jsx(TableStatus, {
        status: tableStatus,
        size: size,
        errorButtonProps: errorButtonProps,
        errorMessage: errorMessage,
        emptyMessage: emptyMessage
      }), !tableStatus && pagination && /*#__PURE__*/_jsx(Pagination, {
        rowsPerPageOptions: rowsPerPageOptions,
        pagination: tablePagination,
        data: _extends({}, data, {
          id: id
        })
      })]
    })
  });
});
Table.displayName = "Table";
export default Table;